import React from 'react';
import { StyleSheet } from 'react-native';
import { useLocation } from 'react-router';
import { DNABox, DNAButton, DNADivider, DNAText, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import { CommonIcon, DNAChip } from 'src/components/DNA';
import { useAddMeeting, useIsMeetingLocked } from './AddMeetingProvider';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { CRMConnectionStatus } from 'src/state/machines/CRM/crmMachineTypes';
import {
  CRMOpenCallButton,
  CRMSubmitButton,
  SaveToBeaconButton,
  useCanDeleteMetting,
} from './AddMeetingFooter';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

export const styles = StyleSheet.create({
  titleContainer: {
    backgroundColor: colors['color-text-white'],
    height: 56,
    paddingLeft: 16,
    paddingRight: 16,
  },
  title: {
    color: colors['color-gray-600'],
  },
  contentContainer: {
    paddingLeft: 24,
    paddingRight: 24,
  },
})

const AddMeetingHeaderDesktop = () => {
  const { onCancel } = useAddMeeting()
  const { pathname } = useLocation()
  const isHubsRoute = pathname.includes('hubs')

  return (
    <DNABox appearance="col">
      <DNABox alignY="center" appearance="row" spacing="between" style={styles.titleContainer}>
        <DNAText bold h5 style={styles.title}>Meeting Event</DNAText>
        <Iffy is={!isHubsRoute}>
          <DNAButton
            appearance="ghost"
            status="tertiary"
            iconLeft="x"
            onPress={onCancel}
          />
        </Iffy>
      </DNABox>
      <DNADivider />
    </DNABox>
  )
}

const AddMeetingHeaderHubTablet: React.FC = () => {
  const { onCancel } = useAddMeeting()

  return (
    <DNABox appearance="col">
      <DNABox alignY="center" appearance="row" spacing="between" style={styles.titleContainer}>
        <DNAText bold h5 style={styles.title}>Meeting Event</DNAText>
        <DNAButton
          appearance="ghost"
          status="subtle"
          size="xl"
          iconLeft="x"
          onPress={onCancel}
        />
      </DNABox>
      <DNADivider />
    </DNABox>
  )
}

const AddMeetingHeaderTablet = () => {
  const {
    onCancel,
    meetingORM,
    onDelete,
    isSubmitting,
    isReadOnly,
  } = useAddMeeting()
  const { pathname } = useLocation()
  const isHubsRoute = pathname.includes('hubs')

  const dateLastSyncedAt = new Date(meetingORM?.model.crmRecord?.lastSyncedAt || '');
  const lastSyncedAt = (!isReadOnly ? 'Saved to CRM on ' : '') + dateLastSyncedAt.toLocaleDateString() + ' at ' +
    dateLastSyncedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const { connectionStatus, isAutomaticLoginEnabled, isCRMConfigEnabled } = useCRMStatus();
  const hideMeetingFormButtons = useFeatureFlags('BEAC_7919_hide_save_to_beacon_button');
  const isMeetingLocked = useIsMeetingLocked(meetingORM)
  const shouldUsePrimaryCRMAction = hideMeetingFormButtons && isCRMConfigEnabled;

  const { deleteDisabled } = useCanDeleteMetting(meetingORM);
  if (isHubsRoute) return <AddMeetingHeaderHubTablet />
  return (
    <DNABox appearance="col">
      <DNABox alignY="center" appearance="row" spacing="md" childFill={2} style={styles.titleContainer}>
        <DNAButton
          appearance="ghost"
          status="tertiary"
          iconLeft="x"
          onPress={onCancel}
          disabled={isSubmitting}
        />
        <DNAText bold h5 style={styles.title}>Meeting Event</DNAText>
        <DNABox alignX="end" spacing="sm" alignY="center">
          <Iffy is={isReadOnly}>
            <DNABox spacing="sm" alignY="center">
              {/* Open in CRM */}
              <Iffy is={meetingORM?.model.crmRecord?.crmCallId}>
                <CRMOpenCallButton meetingORM={meetingORM} />
              </Iffy>
              <CommonIcon variant="lock" />
              <DNAText status="tertiary">
                Submitted to CRM on {lastSyncedAt} and is now locked.
              </DNAText>
            </DNABox>
          </Iffy>
          <Iffy is={meetingORM?.model.crmRecord?.crmCallId && !isReadOnly}>
            <DNAChip.Beacon children={lastSyncedAt} />
          </Iffy>
          <Iffy is={!isReadOnly}>
            {/* Delete Button */}
            <DNAButton
              appearance="outline"
              status="tertiary"
              iconRight="trash"
              onPress={onDelete}
              disabled={isMeetingLocked || deleteDisabled}
            />
          </Iffy>
          {/* Open in CRM */}
          <Iffy is={meetingORM?.model.crmRecord?.crmCallId && !isReadOnly}>
            <CRMOpenCallButton meetingORM={meetingORM} />
          </Iffy>
          {/* Secondary Save Button */}
          <Iffy is={!isReadOnly && shouldUsePrimaryCRMAction}>
            <SaveToBeaconButton
              disabled={isSubmitting}
              isSecondary
            />
          </Iffy>
          {/* CRM Submit Button */}
          <Iffy
            is={
              (shouldUsePrimaryCRMAction
                ? isCRMConfigEnabled
                : connectionStatus === CRMConnectionStatus.CONNECTED || isAutomaticLoginEnabled) &&
              !isReadOnly
            }
          >
            <CRMSubmitButton />
          </Iffy>
          {/* Save Button */}
          <Iffy is={!isReadOnly && !shouldUsePrimaryCRMAction}>
            <SaveToBeaconButton
              disabled={isSubmitting}
            />
          </Iffy>
        </DNABox>
      </DNABox>
      <DNADivider />
    </DNABox>
  )
}

type DeviceModeVariant = Record<DeviceMode, React.ElementType>
const deviceModeVariant:DeviceModeVariant = {
  desktop: AddMeetingHeaderDesktop,
  tablet: AddMeetingHeaderTablet,
}

const AddMeetingHeader = () => {
  const { deviceMode } = useAppSettings()
  const CurrentDeviceModeVariant = deviceModeVariant[deviceMode]

  return <CurrentDeviceModeVariant/>
}

export default AddMeetingHeader
